import React, { useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { LiveInsightsContext, isConnectedState, type ConnectionState } from '../../../../../context/LiveInsightsContextShared';
import { TalkToListenMeter } from './TalkToListenMeter';
import { InterventionSuggestionCard } from './InterventionSuggestionCard';
import { LiveAlertCard } from './LiveAlertCard';
import { RecurrentPatternCard } from './RecurrentPatternCard';
import { CoreBeliefCard } from './CoreBeliefCard';
import { ClinicalInsightData, LiveInsightMessage, TalkToListenRatioData } from '../../../../../types/live-insights';
import { useBreakpoint } from '../../../../../hooks/useBreakpoint';
import { useAiInsightsGate, LockedState } from "../../../../ai_insights";

interface LiveInsightsDashboardProps {
    messages?: LiveInsightMessage[];
    /** @deprecated Use connectionState instead */
    isConnected?: boolean;
    connectionState?: ConnectionState;
    fullPage?: boolean;
}

export const LiveInsightsDashboard: React.FC<LiveInsightsDashboardProps> = ({
    messages: propMessages,
    isConnected: propIsConnected,
    connectionState: propConnectionState,
    fullPage = false
}) => {
    const { t } = useTranslation();
    const context = useContext(LiveInsightsContext);
    const { isMobile } = useBreakpoint();
    const { adopted: aiInsightsAdopted, loading: adoptionLoading } = useAiInsightsGate();
    // Only the AI-driven panels lock; the talk-to-listen meter and recording controls
    // stay live because they are core telemetry, not AI Insights.
    const aiLocked = !adoptionLoading && !aiInsightsAdopted;
    const [activeTab, setActiveTab] = React.useState<'alerts' | 'ratio' | 'beliefs' | 'suggestions'>('alerts');

    // Use props if provided, otherwise fallback to context
    const messages = propMessages ?? (context?.messages as LiveInsightMessage[]) ?? [];
    // Support both new connectionState prop and legacy boolean isConnected prop
    const resolvedConnectionState: ConnectionState =
        propConnectionState ?? context?.connectionState ?? 'idle';
    const isConnected = propIsConnected ?? isConnectedState(resolvedConnectionState);

    const ratioData = [...messages].reverse().find((m) =>
        m.type === 'TalkToListenRatio'
    ) as TalkToListenRatioData | undefined;

    if (ratioData && !ratioData.therapist_ratio && (ratioData as unknown as { metadata_json: TalkToListenRatioData }).metadata_json) {
        // Map from metadata_json if it's the raw model
        const raw = (ratioData as unknown as { metadata_json: TalkToListenRatioData }).metadata_json;
        (ratioData as TalkToListenRatioData).therapist_ratio = raw.therapist_ratio;
        (ratioData as TalkToListenRatioData).patient_ratio = raw.patient_ratio;
        (ratioData as TalkToListenRatioData).warning = raw.warning;
    }

    // Separate alerts from general feed items
    const allInsights = (messages.filter((m) =>
        m.type === 'ClinicalInsight'
    ) as ClinicalInsightData[]).reverse();

    const alerts = allInsights.filter(insight =>
        insight.insight_type === 'status' ||
        insight.metadata?.type === 'ClinicalAlert'
    );

    const feed = allInsights.filter(insight =>
        insight.insight_type !== 'status' &&
        insight.metadata?.type !== 'ClinicalAlert'
    );

    const coreBeliefs = feed.filter(insight =>
        insight.metadata?.type === 'CoreBelief'
    );

    const patterns = feed.filter(insight =>
        insight.metadata?.type === 'RecurrentPattern'
    );

    const suggestions = feed.filter(insight =>
        !['CoreBelief', 'RecurrentPattern'].includes(insight.metadata?.type || '')
    );

    return (
        <div className={`flex flex-col gap-6 h-full ${fullPage ? 'w-full bg-transparent' : 'bg-white rounded-2xl shadow-sm border border-gray-100'} p-4 overflow-y-auto custom-scrollbar`}>
            {!fullPage && (
                <div className="flex items-center justify-between pb-2 border-b border-slate-50 mb-2 pr-12">
                    <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-indigo-500" />
                        {t('session.liveInsights.title', 'Live Insights')}
                    </h3>
                    <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full backdrop-blur-sm border ${isConnected ? 'bg-emerald-50/50 border-emerald-100 text-emerald-600' : 'bg-slate-100 border-slate-200 text-slate-500'
                        }`}>
                        <div className={`w-1.5 h-1.5 rounded-full shadow-sm ${isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
                        <span className="text-xs font-bold uppercase tracking-wider">{isConnected ? 'Live' : 'Offline'}</span>
                    </div>
                </div>
            )}

            {isMobile && fullPage && (
                <div className="flex items-center gap-1 bg-slate-100/50 p-1 rounded-xl mb-2 overflow-x-auto no-scrollbar">
                    <button 
                        onClick={() => setActiveTab('alerts')}
                        className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all
                        ${activeTab === 'alerts' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        {t('session.liveInsights.alertsTitle')}
                    </button>
                    <button 
                        onClick={() => setActiveTab('ratio')}
                        className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all
                        ${activeTab === 'ratio' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        {t('settings.liveDashboard.talkToListenRatio')}
                    </button>
                    <button 
                        onClick={() => setActiveTab('beliefs')}
                        className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all
                        ${activeTab === 'beliefs' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        Insight
                    </button>
                    <button 
                        onClick={() => setActiveTab('suggestions')}
                        className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all
                        ${activeTab === 'suggestions' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        {t('session.liveInsights.suggestionsTitle')}
                    </button>
                </div>
            )}

            {!fullPage && ratioData && (
                <div className="">
                    <TalkToListenMeter data={ratioData} pieChart={fullPage} />
                </div>
            )}

            <div className={fullPage ? (isMobile ? 'flex flex-col gap-6' : 'grid grid-cols-1 xl:grid-cols-2 gap-8 max-w-7xl mx-auto w-full') : 'space-y-6'}>
                {/* ROW 1 LEFT: Talk to Listen Ratio (FullPage only) */}
                {fullPage && ratioData && (!isMobile || activeTab === 'ratio') && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 px-1">
                            <div className="h-px flex-1 bg-slate-200/60" />
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2">
                                {t('settings.liveDashboard.talkToListenRatio')}
                            </span>
                            <div className="h-px flex-1 bg-slate-200/60" />
                        </div>
                        <TalkToListenMeter data={ratioData} pieChart={true} />
                    </div>
                )}

                {/* ROW 1 RIGHT: Clinical Alerts */}
                {(!isMobile || activeTab === 'alerts' || !fullPage) && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 px-1">
                            <div className="h-px flex-1 bg-slate-200/60" />
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2">
                                {t('session.liveInsights.alertsTitle')}
                            </span>
                            <div className="h-px flex-1 bg-slate-200/60" />
                        </div>

                        <div className="flex flex-col gap-3 min-h-[40px]">
                            {aiLocked && <LockedState title={t('aiInsights.locked.liveInsights')} compact />}
                            <AnimatePresence mode="popLayout">
                                {aiLocked ? null : alerts.length > 0 ? (
                                    alerts.slice(0, fullPage ? 10 : 3).map((alert, idx) => (
                                        <motion.div
                                            key={`alert-${idx}`}
                                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                                            animate={{ opacity: 1, scale: 1, y: 0 }}
                                            exit={{ opacity: 0, scale: 0.95 }}
                                            transition={{ type: "spring", damping: 20, stiffness: 300 }}
                                        >
                                            <LiveAlertCard data={alert} />
                                        </motion.div>
                                    ))
                                ) : (
                                    <p className="text-xs text-slate-400 text-center italic py-2">
                                        {t('session.liveInsights.noAlerts')}
                                    </p>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                )}

                {/* ROW 2 LEFT: Core Beliefs (FullPage only) */}
                {fullPage && (!isMobile || activeTab === 'beliefs') && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 px-1">
                            <div className="h-px flex-1 bg-slate-200/60" />
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2">
                                {t('session.liveInsights.beliefsTitle', 'Core Beliefs')}
                            </span>
                            <div className="h-px flex-1 bg-slate-200/60" />
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                            {aiLocked ? (
                                <LockedState title={t('aiInsights.locked.liveInsights')} compact />
                            ) : coreBeliefs.length > 0 ? (
                                coreBeliefs.map((insight, idx) => (
                                    <CoreBeliefCard key={`belief-${idx}`} data={insight} />
                                ))
                            ) : (
                                <p className="text-xs text-slate-400 text-center italic py-2">
                                    {t('session.liveInsights.noActivity')}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* ROW 2 RIGHT: Recurrent Patterns (FullPage only) */}
                {fullPage && (!isMobile || activeTab === 'beliefs') && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 px-1">
                            <div className="h-px flex-1 bg-slate-200/60" />
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2">
                                {t('session.liveInsights.patternsTitle', 'Recurrent Patterns')}
                            </span>
                            <div className="h-px flex-1 bg-slate-200/60" />
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                            {aiLocked ? (
                                <LockedState title={t('aiInsights.locked.liveInsights')} compact />
                            ) : patterns.length > 0 ? (
                                patterns.map((insight, idx) => (
                                    <RecurrentPatternCard key={`pattern-${idx}`} data={insight} />
                                ))
                            ) : (
                                <p className="text-xs text-slate-400 text-center italic py-2">
                                    {t('session.liveInsights.noActivity')}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* ACTIVITY FEED (Sidebar Mode) */}
                {!fullPage && (
                    <div className="space-y-4 flex-1">
                        <div className="flex items-center gap-2 px-1">
                            <div className="h-px flex-1 bg-slate-200/60" />
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2">
                                {t('session.liveInsights.activityTitle')}
                            </span>
                            <div className="h-px flex-1 bg-slate-200/60" />
                        </div>

                        <div className="flex flex-col gap-4 pb-8">
                            {aiLocked && <LockedState title={t('aiInsights.locked.liveInsights')} compact />}
                            <AnimatePresence mode="popLayout">
                                {aiLocked ? null : feed.length > 0 ? (
                                    feed.slice(0, 8).map((insight, idx) => {
                                        const type = insight.metadata?.type;
                                        let CardComponent = InterventionSuggestionCard;

                                        if (type === 'RecurrentPattern') CardComponent = RecurrentPatternCard;
                                        if (type === 'CoreBelief') CardComponent = CoreBeliefCard;

                                        return (
                                            <motion.div
                                                key={`feed-${idx}`}
                                                initial={{ opacity: 0, x: 10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, scale: 0.9 }}
                                                transition={{ delay: idx * 0.05 }}
                                            >
                                                <CardComponent data={insight} />
                                            </motion.div>
                                        );
                                    })
                                ) : (
                                    <p className="text-xs text-slate-400 text-center italic py-2">
                                        {t('session.liveInsights.noActivity')}
                                    </p>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                )}

                {/* SUGGESTIONS (FullPage Mode - Full Width) */}
                {fullPage && !aiLocked && suggestions.length > 0 && (!isMobile || activeTab === 'suggestions') && (
                    <div className="col-span-full space-y-4">
                        <div className="flex items-center gap-2 px-1">
                            <div className="h-px flex-1 bg-slate-200/60" />
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2">
                                {t('session.liveInsights.suggestionsTitle', 'Suggestions')}
                            </span>
                            <div className="h-px flex-1 bg-slate-200/60" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {suggestions.map((insight, idx) => (
                                <InterventionSuggestionCard key={`suggestion-${idx}`} data={insight} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
